import { colors } from '@apollo/space-kit/colors';
import classnames from 'classnames';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { createPortal } from 'react-dom';

export const LineDiffMarkers = ({
  editor,
  changedLines,
  type,
  editorTextValue,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  changedLines: number[];
  type: 'added' | 'removed' | 'modified';
  editorTextValue: string | undefined;
}) => {
  return (
    <>
      {changedLines.map((lineNumber) => (
        <LineDiffMarker
          key={lineNumber}
          lineNumber={lineNumber}
          editor={editor}
          type={type}
          editorTextValue={editorTextValue}
        />
      ))}
    </>
  );
};

const LineDiffMarker = ({
  editor,
  lineNumber,
  type,
  editorTextValue,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  lineNumber: number;
  type: 'added' | 'removed' | 'modified';
  editorTextValue: string | undefined;
}) => {
  const marginDomNode = React.useMemo(() => document.createElement('div'), []);

  const currentViewZone = React.useRef<{
    zoneId: string;
    zone: monacoEditor.editor.IViewZone;
  }>();

  const afterLineNumber = lineNumber - 1;

  React.useEffect(() => {
    // A view zone is a full horizontal rectangle that 'pushes' text down. The editor reserves space for view zones when rendering.
    // we position ours absolutely so that they don't push anything down and are just rendered over a line
    editor.changeViewZones((changeAccessor) => {
      const newZone = {
        afterLineNumber,
        heightInLines: 0,
        domNode: document.createElement('div'),
        marginDomNode,
      };
      currentViewZone.current = {
        zone: newZone,
        zoneId: changeAccessor.addZone(newZone),
      };
    });

    return () => {
      const lastZone = currentViewZone.current;
      if (!lastZone) return;
      editor.changeViewZones((changeAccessor) => {
        changeAccessor.removeZone(lastZone.zoneId);
      });
      currentViewZone.current = undefined;
    };
    // when models change, view zones are lost so it needs to be in the dependency array
  }, [afterLineNumber, editor, marginDomNode, editorTextValue]);

  return (
    <>
      {createPortal(
        <div
          className={classnames(
            'w-full',
            type === 'removed' ? 'pr-4' : 'pr-[18px]',
          )}
        >
          <div
            className={classnames('ml-auto', {
              'h-5 w-1 bg-green-dark': type === 'added',
              'h-5 w-1': type === 'modified',
              '-mt-1 size-0 border-y-4 border-l-[6px] border-transparent border-l-red':
                type === 'removed',
            })}
            {...(type === 'modified'
              ? {
                  style: {
                    background: `repeating-linear-gradient(45deg, ${colors.blue.base}, ${colors.blue.base} 2px, transparent 2px, transparent 3px)`,
                  },
                }
              : {})}
          />
        </div>,
        marginDomNode,
      )}
    </>
  );
};
